import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl } from "@/lib/api";
import { fmtDate, EVENT_STATUS_LABEL, usePageMeta } from "@/lib/content";
import { Reveal } from "@/components/site/Reveal";

export default function EventDetail() {
  const { slug } = useParams();
  const { data: ev, isLoading, isError } = useQuery({
    queryKey: ["event", slug],
    queryFn: async () => (await api.get(`/public/dates/slug/${slug}`)).data,
    retry: false,
  });
  const d = ev ? fmtDate(ev.date) : null;
  const title = ev ? `SEIMEN DEXTER — Live: ${ev.title}` : "SEIMEN DEXTER — Dates";

  usePageMeta(
    title,
    ev
      ? `${ev.title} — ${ev.venue}, ${ev.city}. SEIMEN DEXTER live: Hard / Industrial Techno, 165 BPM.`
      : "Live dates of SEIMEN DEXTER."
  );

  useEffect(() => {
    if (!ev) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Event",
      name: ev.title,
      startDate: ev.time ? `${ev.date}T${ev.time}:00` : ev.date,
      eventStatus:
        ev.effective_status === "cancelled"
          ? "https://schema.org/EventCancelled"
          : "https://schema.org/EventScheduled",
      location: {
        "@type": "Place",
        name: ev.venue,
        address: [ev.city, ev.country].filter(Boolean).join(", "),
      },
      performer: { "@type": "MusicGroup", name: "SEIMEN DEXTER" },
      image: ev.image_url ? [new URL(fileUrl(ev.image_url), window.location.origin).href] : undefined,
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [ev]);

  if (isLoading) {
    return <div className="px-5 md:px-10 pt-40 pb-24 text-white/40 text-sm">LOADING…</div>;
  }

  if (isError || !ev) {
    return (
      <div className="px-5 md:px-10 pt-40 pb-24">
        <p className="font-display text-6xl">EVENT NOT FOUND.</p>
        <Link data-testid="event-not-found-back" to="/dates" className="btn-ghost mt-10">
          ALL DATES
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-24 md:pt-32 pb-24 md:pb-36">
      <div className="px-5 md:px-10 pt-6 md:pt-10">
        <Link data-testid="event-back" to="/dates" className="inline-flex items-center gap-3 text-[11px] tracking-[0.3em] text-white/50 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> ALL DATES
        </Link>
      </div>

      {ev.image_url && (
        <Reveal className="px-5 md:px-10 mt-10">
          <img
            src={fileUrl(ev.image_url)}
            alt={`${ev.title} — flyer`}
            className="w-full max-h-[70vh] object-contain border border-line bg-panel"
            draggable="false"
          />
        </Reveal>
      )}

      <div className="px-5 md:px-10 mt-12 md:mt-16 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-3">
          <p className="font-display text-7xl md:text-8xl leading-none">
            {d.day}
            <span className="text-bloodbright">.</span>
          </p>
          <p className="kicker mt-2">
            {d.mon} {d.year}
            {ev.time ? ` — ${ev.time}` : ""}
          </p>
          <span
            data-testid="event-status-badge"
            className={`badge mt-6 ${ev.effective_status === "cancelled" ? "text-red-400" : ""} ${
              ev.effective_status === "sold_out" ? "badge-accent" : ""
            }`}
          >
            {EVENT_STATUS_LABEL[ev.effective_status] || "UPCOMING"}
          </span>
        </div>
        <div className="md:col-span-9">
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95]">{ev.title}</h1>
          <div className="mt-8 md:mt-10 text-sm md:text-base text-white/70 space-y-2">
            <p>{ev.venue}</p>
            <p>{[ev.city, ev.country].filter(Boolean).join(", ")}</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3 md:gap-4">
            {ev.ticket_url && ev.effective_status !== "past" && ev.effective_status !== "cancelled" && (
              <a data-testid="event-tickets" href={ev.ticket_url} target="_blank" rel="noreferrer" className="btn-primary">
                TICKETS
              </a>
            )}
            {ev.info_url && (
              <a data-testid="event-info" href={ev.info_url} target="_blank" rel="noreferrer" className="btn-ghost">
                EVENT INFO
              </a>
            )}
            <Link data-testid="event-booking" to="/booking" className="btn-ghost">
              BOOKING REQUEST
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
